import { APIErrorXHR } from './api-error'
import { request, requestJSON } from './request'
import { FORUM_ASSETS_URL } from '@/utils/env'

export interface Asset {
  pk: number
  name: string
  filename: string
  size: number
  content_type?: string
  hash?: string
  uuid?: string
  label?: string
  notes?: string
  platform?: string
  order: number | undefined
  file?: string
  url: string
  path?: string
  access_url?: string
  is_premium: boolean
  authorized_download?: boolean
  protection_unlock_url?: string
  // unlock_url?: string
  asset_meta?: AssetMeta
  asset_group?: number
}

interface AssetMeta {
  md5?: string
  free_download?: boolean
}

export async function getVersionAsset (assetId: number): Promise<Asset> {
  return await requestJSON(`/api/project-assets/${assetId}/`)
}

export async function getAssetDownload (assetId: number): Promise<Object[]> {
  return await requestJSON(`/api/project-assets/${assetId}/asset_download/`)
}

export class AbortError extends Error {}

export interface UploadAssetConfig {
  signal?: AbortSignal
  onProgress?: (e: ProgressEvent<XMLHttpRequestEventTarget>) => void
}

export async function uploadAsset (data: FormData, config?: UploadAssetConfig): Promise<Asset> {
  // ReadableStream in fetch's request body (which allows progress status)
  // is *not* implemented in browsers, as of 2021.
  // see :
  // - https://stackoverflow.com/questions/35711724/upload-progress-indicators-for-fetch
  // - https://github.com/whatwg/fetch/pull/425
  // - https://stackoverflow.com/questions/40939857/fetch-with-readablestream-as-request-body
  // - https://caniuse.com/mdn-api_request_request_readablestream_request_body
  // - https://w3c-test.org/fetch/api/basic/request-upload.any.html
  // We use XMLHttpRequest as an alternative for now.

  return await new Promise((resolve, reject) => {
    const url = new URL('/upload', FORUM_ASSETS_URL)

    const request = new XMLHttpRequest()
    request.responseType = 'json'
    request.open('POST', url.toString())
    // if (csrftoken != undefined) {
    //   request.setRequestHeader('X-CSRF-Token', csrftoken)
    // }
    request.withCredentials = true
    // update progress value
    if (config?.onProgress !== undefined) {
      request.upload.addEventListener('progress', config.onProgress)
    }
    request.addEventListener('error', () => {
      reject(new APIErrorXHR(request))
    })
    request.addEventListener('load', () => {
      if (request.status >= 300) {
        reject(new APIErrorXHR(request))
      }
      resolve(request.response)
    })
    request.addEventListener('abort', () => {
      reject(new AbortError())
    })
    if (config?.signal !== undefined) {
      config.signal.addEventListener('abort', () => { request.abort() })
    }
    // start request
    request.send(data)
  })
}

export interface EditVersionAsset {
  name?: string
  label?: string
  notes?: string
  platform?: string
  is_premium?: boolean
  // unlock_url?: string
}

export async function editVersionAsset (assetId: number, body: EditVersionAsset): Promise<void> {
  // NB: Not using requestJSON because this request's response body is empty
  await request(`/api/project-assets/${assetId}/`, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' }
  })
}

export interface EditAssetAccessUrl {
  name?: string
  access_url?: string
}

export async function editAssetAccessUrl (assetId: number, body: EditAssetAccessUrl): Promise<void> {
  // NB: Not using requestJSON because this request's response body is empty
  await request(`/api/project-assets/${assetId}/`, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' }
  })
}

export interface EditAssetOrder {
  name: string
  order: number | null
}

export async function editProjectAssetOrder (assetId: number, body: EditAssetOrder): Promise<void> {
  // NB: Not using requestJSON because this request's response body is empty
  await request(`/api/project-assets/${assetId}/`, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' }
  })
}

export interface UpdateAssetDownloadCounter {
  asset: number
}

export async function updateAssetDownloadCounter (assetId: number, body: UpdateAssetDownloadCounter): Promise<void> {
  // NB: Not using requestJSON because this request's response body is empty
  await request(`/api/project-assets/${assetId}/asset_download/`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' }
  })
}

export async function deleteVersionAsset (assetId: number): Promise<void> {
  await request(`/api/project-assets/${assetId}/`, { method: 'DELETE' })
}
